import { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, LineChart } from "lucide-react";
import { api } from "../../lib/api";
import { useLiveMarket } from "../../context/LiveMarketContext";
import { Sparkline } from "./Sparkline";

const symbol = (p) => {
  const map = { "Google Sheets": "GSHT", PowerPoint: "PPTX" };
  return (map[p.software_type] || p.software_type.slice(0, 4)).toUpperCase();
};

export const MarketBoard = ({ onSelect }) => {
  const [products, setProducts] = useState([]);
  const { prices, dirs, history } = useLiveMarket();

  useEffect(() => {
    api.products({ sort: "savings" }).then(setProducts).catch(() => {});
  }, []);

  if (!products.length) return null;

  const rows = products
    .map((p) => {
      const live = prices[p.id];
      const disc = live?.discount_pct ?? p.pricing?.discount_pct ?? 0;
      const market = live?.live_competitor ?? p.pricing?.live_competitor ?? 0;
      return { ...p, disc, market };
    })
    .sort((a, b) => b.disc - a.disc);

  return (
    <section className="border-b border-white/10 bg-slate-950 py-16 lg:py-20">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="mb-6 flex items-end justify-between border-b border-white/10 pb-5">
          <div>
            <span className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-emerald-400">
              <LineChart className="h-3.5 w-3.5" /> Live Leaderboard
            </span>
            <h2 className="mt-2 font-display text-3xl font-black uppercase tracking-tight text-white sm:text-4xl">
              The Market Board
            </h2>
          </div>
          <span className="hidden font-mono text-[11px] uppercase tracking-wider text-slate-500 sm:block">
            Ranked by discount · updates live
          </span>
        </div>

        <div data-testid="market-board" className="overflow-x-auto border border-white/10">
          <table className="w-full min-w-[640px] border-collapse">
            <thead>
              <tr className="border-b border-white/10 bg-slate-900 font-mono text-[10px] uppercase tracking-wider text-slate-500">
                <th className="px-4 py-2.5 text-left">#</th>
                <th className="px-4 py-2.5 text-left">Symbol</th>
                <th className="px-4 py-2.5 text-left">Template</th>
                <th className="px-4 py-2.5 text-right">Price</th>
                <th className="px-4 py-2.5 text-right">Market</th>
                <th className="px-4 py-2.5 text-right">Discount</th>
                <th className="px-4 py-2.5 text-right">Trend</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p, i) => {
                const dir = dirs[p.id];
                return (
                  <tr
                    key={p.id}
                    data-testid={`board-row-${p.id}`}
                    onClick={() => onSelect && onSelect(p)}
                    className="cursor-pointer border-b border-white/5 text-sm transition-colors duration-150 last:border-b-0 hover:bg-emerald-500/5"
                  >
                    <td className="px-4 py-3 font-mono text-slate-500">{String(i + 1).padStart(2, "0")}</td>
                    <td className="px-4 py-3 font-mono font-bold tracking-wider text-white">{symbol(p)}</td>
                    <td className="px-4 py-3 text-slate-200">
                      <span className="block max-w-[220px] truncate">{p.title}</span>
                      <span className="font-mono text-[10px] uppercase tracking-wider text-slate-600">{p.tier}</span>
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-bold text-emerald-400">
                      {p.base_price === 0 ? "FREE" : `$${p.base_price}`}
                    </td>
                    <td className={`px-4 py-3 text-right font-mono ${dir === "up" ? "text-emerald-400" : dir === "down" ? "text-red-400" : "text-slate-500"}`}>
                      ${p.market}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="inline-flex items-center gap-1 font-mono text-xs font-bold text-emerald-400">
                        <TrendingUp className="h-3 w-3" /> +{p.disc}%
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end">
                        <Sparkline data={history[p.id]} up={dir !== "down"} width={72} height={22} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

export default MarketBoard;
